import { Box } from "@mui/material"
import type { FC } from "react"
import React from "react"
import { useFormContext } from "react-hook-form"

import { FormDatePicker, FormMultiSelect, FormSelect } from "@/components/UiKit"
import { PriceChangeType } from "@/graphql/codegen/graphql"
import { priceChangeTypeLabelMap } from "@/screens/Pricing/constants/priceChangeLabelMap"
import { useGetPriceChangeFilterOptions } from "@/screens/Pricing/hooks/useGetPriceChangeFilterOptions"
import { mapLookupItemsToDropdownOptions } from "@/screens/Pricing/utils/lookup"
import { toNumberOrNull } from "@/utils/forms.ts"

const changeTypeOptions = Object.values(PriceChangeType).map((value) => ({
  value: value.toString(),
  label: priceChangeTypeLabelMap.get(value) ?? value,
}))

const PriceChangeFilterForm: FC = () => {
  const { control, watch } = useFormContext()

  const changeTypeFieldValue = watch("changeType")
  const supplierFieldValue = watch("supplierId")
  const customerFieldValue = watch("customerIds")
  const productCategoryFieldValue = watch("productCategoryId")
  const productClassFieldValue = watch("productClassId")
  const specificProductFieldValue: string[] = watch("specificProductIds")

  const { filterOptions } = useGetPriceChangeFilterOptions({
    changeType: changeTypeFieldValue,
    supplierId: supplierFieldValue,
    customerIds: customerFieldValue,
    productCategoryId: productCategoryFieldValue,
    productClassId: productClassFieldValue,
    specificProductIds: specificProductFieldValue
      .map((specificProductId) => toNumberOrNull(specificProductId))
      .filter((v) => v !== null),
  })

  return (
    <form>
      <Box className='flex flex-row justify-between gap-3'>
        <FormSelect
          control={control}
          name='changeType'
          label='Change Type'
          placeholder='Select Change Type'
          options={changeTypeOptions}
          required
          data-testid='pricing.change-form.change-type-field'
        />

        <FormSelect
          control={control}
          name='supplierId'
          label='Supplier'
          placeholder='Select Supplier'
          disabled={!changeTypeFieldValue}
          options={mapLookupItemsToDropdownOptions(filterOptions?.suppliers)}
          data-testid='pricing.change-form.supplier-field'
        />

        {changeTypeFieldValue === PriceChangeType.SellingPrice && (
          <FormMultiSelect
            control={control}
            name='customerIds'
            label='Customer'
            placeholder='Select Customer'
            disabled={!changeTypeFieldValue}
            options={mapLookupItemsToDropdownOptions(filterOptions?.customers)}
            data-testid='pricing.change-form.customer-field'
          />
        )}

        <FormSelect
          control={control}
          name='productCategoryId'
          label='Product Category'
          placeholder='Select Product Category'
          disabled={!changeTypeFieldValue}
          options={mapLookupItemsToDropdownOptions(filterOptions?.productCategories)}
          data-testid='pricing.change-form.product-category-field'
        />

        <FormSelect
          control={control}
          name='productClassId'
          label='Product Class'
          placeholder='Select Product Class'
          disabled={!changeTypeFieldValue}
          options={mapLookupItemsToDropdownOptions(filterOptions?.productClasses)}
          data-testid='pricing.change-form.product-class-field'
        />

        <FormMultiSelect
          control={control}
          name='specificProductIds'
          label='Specific Product(s)'
          placeholder='Select Specific Product(s)'
          disabled={!changeTypeFieldValue}
          options={mapLookupItemsToDropdownOptions(filterOptions?.products)}
          data-testid='pricing.change-form.specific-products-field'
        />
      </Box>

      <Box className='mt-6 flex flex-row justify-between'>
        <Box className='flex flex-row gap-3'>
          <FormSelect
            control={control}
            name='shipFrom'
            label='Ship From'
            placeholder='Select Ship From'
            disabled={!changeTypeFieldValue}
            options={mapLookupItemsToDropdownOptions(filterOptions?.shipFromWarehouseAreas)}
            data-testid='pricing.change-form.ship-from-field'
          />

          <FormSelect
            control={control}
            name='shipTo'
            label='Ship To'
            placeholder='Select Ship To'
            disabled={!changeTypeFieldValue || !filterOptions?.shipToLocations?.length}
            options={mapLookupItemsToDropdownOptions(filterOptions?.shipToLocations)}
            data-testid='pricing.change-form.ship-to-field'
          />

          <FormDatePicker
            control={control}
            name='effectiveDate'
            label='Effective Date'
            disabled={!changeTypeFieldValue}
            data-testid='pricing.change-form.effective-date-field'
          />
        </Box>
      </Box>
    </form>
  )
}

export default PriceChangeFilterForm
